import { createContext, useContext, useEffect, useState, Suspense, lazy } from "react";
import { Routes, Route, useLocation } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { EASE } from "@/lib/motion";
import { TopNav } from "@/components/nav/TopNav";
import { MonumentFooter } from "@/components/footer/MonumentFooter";
import { CursorProvider } from "@/components/ui-custom/Cursor";
import { Preloader } from "@/components/ui-custom/Preloader";
import { SmoothScroll, scrollToTop, scrollToEl } from "./SmoothScroll";
import { Toaster } from "@/components/ui/sonner";

const Home = lazy(() => import("@/pages/Home"));
const Leistungen = lazy(() => import("@/pages/Leistungen"));
const Industrie = lazy(() => import("@/pages/Industrie"));
const Projekte = lazy(() => import("@/pages/Projekte"));
const ProjektDetail = lazy(() => import("@/pages/ProjektDetail"));
const Unternehmen = lazy(() => import("@/pages/Unternehmen"));
const Kontakt = lazy(() => import("@/pages/Kontakt"));
const Impressum = lazy(() => import("@/pages/Impressum"));
const Datenschutz = lazy(() => import("@/pages/Datenschutz"));
const AdminAnfragen = lazy(() => import("@/pages/AdminAnfragen"));
const NotFound = lazy(() => import("@/pages/NotFound"));

// ---- nav theme context: pages with dark heroes set 'light' ----
const NavThemeCtx = createContext({ theme: "dark", setTheme: () => {} });
export const useNavTheme = (theme) => {
  const { setTheme } = useContext(NavThemeCtx);
  useEffect(() => {
    setTheme(theme);
    return () => setTheme("dark");
  }, [theme, setTheme]);
};
export const useNavThemeValue = () => useContext(NavThemeCtx).theme;

const COLS = 6;
const TransitionGrid = ({ phase, onInComplete, onOutComplete }) => {
  if (phase === "idle") return null;
  const isIn = phase === "in";
  return (
    <div
      aria-hidden="true"
      data-testid="page-transition"
      className="fixed inset-0 z-[90] grid pointer-events-auto"
      style={{ gridTemplateColumns: `repeat(${COLS}, 1fr)` }}
    >
      {Array.from({ length: COLS }).map((_, i) => (
        <motion.div
          key={i}
          className="bg-ink border-r border-paper/10 last:border-r-0"
          style={{ originY: isIn ? 1 : 0 }}
          initial={{ scaleY: isIn ? 0 : 1 }}
          animate={{ scaleY: isIn ? 1 : 0 }}
          transition={{ duration: 0.42, ease: EASE, delay: i * 0.04 }}
          onAnimationComplete={i === COLS - 1 ? (isIn ? onInComplete : onOutComplete) : undefined}
        />
      ))}
    </div>
  );
};

const AppRoutes = ({ location }) => (
  <Suspense fallback={<div className="min-h-[60vh]" />}>
    <Routes location={location}>
      <Route path="/" element={<Home />} />
      <Route path="/leistungen" element={<Leistungen />} />
      <Route path="/industrie" element={<Industrie />} />
      <Route path="/projekte" element={<Projekte />} />
      <Route path="/projekte/:slug" element={<ProjektDetail />} />
      <Route path="/unternehmen" element={<Unternehmen />} />
      <Route path="/kontakt" element={<Kontakt />} />
      <Route path="/impressum" element={<Impressum />} />
      <Route path="/datenschutz" element={<Datenschutz />} />
      <Route path="/admin/anfragen" element={<AdminAnfragen />} />
      <Route path="*" element={<NotFound />} />
    </Routes>
  </Suspense>
);

export const SiteShell = () => {
  const location = useLocation();
  const reduce = useReducedMotion();
  const [displayLocation, setDisplayLocation] = useState(location);
  const [phase, setPhase] = useState("idle");
  const [theme, setTheme] = useState("dark");

  useEffect(() => {
    if (location.pathname !== displayLocation.pathname) {
      if (reduce) {
        setDisplayLocation(location);
        scrollToTop();
      } else {
        setPhase("in");
      }
    } else if (location.key !== displayLocation.key) {
      setDisplayLocation(location);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  const onInComplete = () => {
    setDisplayLocation(location);
    scrollToTop();
    requestAnimationFrame(() => setPhase("out"));
  };
  const onOutComplete = () => setPhase("idle");

  // generic anchor handling (#id): after the page is displayed and on hash changes; retries until the element exists
  useEffect(() => {
    if (phase !== "idle") return undefined;
    let timer = null;
    const run = () => {
      const hash = displayLocation.hash || window.location.hash;
      if (timer) clearInterval(timer);
      if (!hash) return;
      let tries = 0;
      timer = setInterval(() => {
        const el = document.getElementById(decodeURIComponent(hash.slice(1)));
        tries += 1;
        if (el) {
          clearInterval(timer);
          scrollToEl(el, -100);
        } else if (tries > 15) {
          clearInterval(timer);
        }
      }, 250);
    };
    run();
    window.addEventListener("hashchange", run);
    return () => {
      if (timer) clearInterval(timer);
      window.removeEventListener("hashchange", run);
    };
  }, [displayLocation, phase]);

  const isAdmin = displayLocation.pathname.startsWith("/admin");

  return (
    <NavThemeCtx.Provider value={{ theme, setTheme }}>
      <CursorProvider>
        <div className="grain">
          <SmoothScroll />
          <Preloader />
          <a
            href="#main"
            className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[95] focus:bg-ink focus:px-4 focus:py-2 focus:text-paper mono-label"
          >
            Zum Inhalt springen
          </a>
          {!isAdmin && <TopNav />}
          <main id="main">
            <AppRoutes location={displayLocation} />
          </main>
          {!isAdmin && <MonumentFooter />}
          <TransitionGrid phase={phase} onInComplete={onInComplete} onOutComplete={onOutComplete} />
          <Toaster position="bottom-right" toastOptions={{ className: "font-body" }} />
        </div>
      </CursorProvider>
    </NavThemeCtx.Provider>
  );
};
